import { Transform } from '@tiptap/pm/transform';
import type { Node } from '@tiptap/pm/model';
// --- Column width metadata -------------------------------------------------
// Kolombreedtes worden opgeslagen als een metadata-comment boven elke tabel,
// bv. `<!-- colwidth: 40%,null,10% -->`. Elke kolom is een percentage van de
// totale tabelbreedte, of `null` wanneer de gebruiker die kolom niet heeft
// aangepast (de kolom verdeelt dan de resterende ruimte automatisch).
// Tiptap rekent intern in pixels; we converteren %↔px op de laad/opslag-grens.
// Gedeeld tussen de verslag-editor (SummaryEditor) en de sjabloon-editor
// (MarkdownEditor) zodat beide hetzelfde formaat lezen en schrijven.
import { TableMap } from '@tiptap/pm/tables';

export const CELL_MIN_WIDTH = 25; // Tiptap's default cellMinWidth

const COLWIDTH_COMMENT_PATTERN = /^<!--\s*colwidth:\s*(.+?)\s*-->$/i;

export const isColWidthComment = (line: string): boolean =>
    /^<!--\s*colwidth:.*-->$/i.test(line.trim());

// Bouw de metadata-comment voor een tabel, of null wanneer geen enkele kolom
// een expliciete breedte heeft (dan hoort er ook geen comment in de markdown).
export const buildColWidthComment = (tokens: (string | null)[]): string | null => {
    if (!tokens.some((token) => token !== null)) return null;
    return `<!-- colwidth: ${tokens.map((token) => token ?? 'null').join(',')} -->`;
};

// Normaliseer één token uit de comment naar een CSS-waarde of null.
// Een kale getalwaarde wordt als px geïnterpreteerd (backward compat met het
// oude `<!-- colwidth: 200,25,25 -->` formaat).
export const normalizeColWidth = (token: string): string | null => {
    const t = token.trim().toLowerCase();
    if (!t || t === 'null' || t === 'auto') return null;
    const m = t.match(/^(\d+(?:\.\d+)?)(%|px)?$/);
    if (!m) return null;
    return `${m[1]}${m[2] || 'px'}`;
};

export const parseColWidths = (raw: string): (string | null)[] =>
    raw.split(',').map(normalizeColWidth);

// Zet een genormaliseerde token om naar pixels, gegeven de tabelbreedte.
export const colWidthToPx = (token: string | null, tableWidthPx: number): number | null => {
    if (!token) return null;
    const m = token.match(/^(\d+(?:\.\d+)?)(%|px)$/);
    if (!m) return null;
    const value = parseFloat(m[1]);
    const px = m[2] === '%' ? (value / 100) * tableWidthPx : value;
    return Math.max(Math.round(px), CELL_MIN_WIDTH);
};

// Haal per tabel (in documentvolgorde) de colwidth-tokens uit de markdown.
// Spiegelt de tabel-detectie van markdownToHtml zodat de indexen overeenkomen
// met de tabellen die in de editor terechtkomen.
export const extractColWidthTokensByTable = (markdown: string): (string | null)[][] => {
    const result: (string | null)[][] = [];
    let pending: (string | null)[] | null = null;
    let inTable = false;

    for (const rawLine of markdown.split('\n')) {
        const line = rawLine.trim();

        const commentMatch = line.match(COLWIDTH_COMMENT_PATTERN);
        if (commentMatch) {
            pending = parseColWidths(commentMatch[1]);
            continue;
        }

        const isTableRow = line.includes('|') && line.split('|').length > 2;
        const isTableSeparator = /^\s*\|?\s*:?-+:?\s*(\|\s*:?-+:?\s*)*\|?\s*$/.test(line);

        if (isTableRow || isTableSeparator) {
            if (!inTable) {
                inTable = true;
                result.push(pending ?? []);
                pending = null;
            }
        } else if (inTable) {
            inTable = false;
        }
    }

    return result;
};

// Meet per tabel (documentvolgorde) de kolombreedtes uit de live editor-DOM en
// druk ze uit als percentage van de tabelbreedte. Een kolom telt als "gezet"
// wanneer de bijbehorende <col> een expliciete `width:` heeft (Tiptap zet enkel
// `min-width: 25px` voor niet-aangepaste kolommen) — die worden `null`.
export const measureColWidthTokens = (editor: any): (string | null)[][] => {
    const dom = editor?.view?.dom as HTMLElement | undefined;
    if (!dom) return [];

    const tables = Array.from(dom.querySelectorAll('table')) as HTMLElement[];
    const documents: Node[] = [];
    editor.state?.doc.descendants((node: Node) => {
        if (node.type.name !== 'table') return true;
        documents.push(node);
        return false;
    });
    return tables.map((table, tableIndex) => {
        const tableWidth = table.offsetWidth || 0;
        const cols = Array.from(table.querySelectorAll('colgroup > col')) as HTMLElement[];
        const document = documents[tableIndex];
        const map = document && TableMap.get(document);
        return Array.from({ length: map?.width ?? cols.length }, (_, index) => {
            const style = (cols[index]?.getAttribute('style') || '').toLowerCase();
            const match = style.match(/(?:^|[^-])width\s*:\s*(\d+(?:\.\d+)?)px/);
            // Initial hydration can update cell attributes before the colgroup is
            // rebuilt. Keep the explicit model width instead of dropping metadata.
            const cell = map && document?.nodeAt(map.map[index]);
            const column = map ? index - map.colCount(map.map[index]) : 0;
            const width = match ? parseFloat(match[1]) : cell?.attrs.colwidth?.[column];
            if (!width || !tableWidth) return null;
            const pct = Math.round((width / tableWidth) * 100);
            return pct > 0 ? `${pct}%` : null;
        });
    });
};

// Hydrateer de pixel-kolombreedtes in Tiptap's eigen model op basis van de
// opgeslagen colwidth-comment (% → px t.o.v. de gerenderde tabelbreedte).
// Vanaf dat moment beheert Tiptap de breedtes natief; bij opslaan worden ze
// weer naar % gemeten (measureColWidthTokens).
export const hydrateColumnWidths = (editor: any, tokensByTable: (string | null)[][]): void => {
    if (!editor || tokensByTable.length === 0) return;

    const { state, view } = editor;
    const domTables = Array.from(view.dom.querySelectorAll('table')) as HTMLElement[];
    let tr = state.tr;
    let changed = false;
    let tableIndex = 0;

    state.doc.descendants((node: any, pos: number) => {
        if (node.type.name !== 'table') return true;

        const idx = tableIndex++;
        const tokens = tokensByTable[idx];
        const domTable = domTables[idx];
        if (!tokens || tokens.length === 0 || !domTable) return false;

        const tableWidth = domTable.offsetWidth || 0;
        if (!tableWidth) return false;

        const map = TableMap.get(node);
        const tableStart = pos + 1;
        const appliedCells = new Set<number>();

        for (let col = 0; col < map.width; col++) {
            const px = colWidthToPx(tokens[col] ?? null, tableWidth);
            for (let row = 0; row < map.height; row++) {
                const cellRelPos = map.map[row * map.width + col];
                if (appliedCells.has(cellRelPos)) continue;
                appliedCells.add(cellRelPos);

                const cell = node.nodeAt(cellRelPos);
                if (!cell || (cell.attrs.colspan || 1) > 1) continue;

                tr = tr.setNodeMarkup(tableStart + cellRelPos, undefined, {
                    ...cell.attrs,
                    colwidth: px == null ? null : [px],
                });
                changed = true;
            }
        }

        return false; // niet in de tabel afdalen
    });

    if (changed) {
        tr.setMeta('addToHistory', false);
        view.dispatch(tr);
    }
};

export const withColumnWidths = (doc: Node, tokensByTable: (string | null)[][], tableWidths: number[]): Node => {
    if (!tokensByTable.length) return doc;
    const transform = new Transform(doc);
    let tableIndex = 0;
    doc.descendants((table, pos) => {
        if (table.type.name !== 'table') return true;
        const index = tableIndex++;
        const tokens = tokensByTable[index];
        const width = tableWidths[index];
        if (!tokens?.length || !width) return false;
        const map = TableMap.get(table);
        const visited = new Set<number>();
        for (let col = 0; col < map.width; col++) {
            const px = colWidthToPx(tokens[col] ?? null, width);
            for (let row = 0; row < map.height; row++) {
                const offset = map.map[row * map.width + col];
                if (visited.has(offset)) continue;
                visited.add(offset);
                const cell = table.nodeAt(offset);
                if (!cell || (cell.attrs.colspan || 1) > 1) continue;
                const previous = cell.attrs.colwidth;
                if (px === null ? previous == null : previous?.length === 1 && previous[0] === px) continue;
                transform.setNodeMarkup(pos + 1 + offset, undefined, { ...cell.attrs, colwidth: px === null ? null : [px] });
            }
        }
        return false;
    });
    return transform.doc;
};
